import { useState } from 'react';
import PropTypes from 'prop-types';
import { Download, FileUp } from 'lucide-react';
import { Button } from '../ui';
import adminMicroSimService from '../../services/adminMicroSimService';
import MicroSimValidationReport from './MicroSimValidationReport';
import { showError } from '../../utils/toast';

/**
 * Upload a topic (the Excel template or JSON) → check it → save as a draft version.
 * Saving is only offered once the check passes; nothing goes live until it is
 * published from the topic page.
 */
const MicroSimUploadPanel = ({ onSaved }) => {
  const [file, setFile] = useState(null);
  const [changeNote, setChangeNote] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState('');

  const choose = (f) => {
    setFile(f || null);
    setReport(null);
  };

  const check = async () => {
    setBusy('check');
    try {
      const res = await adminMicroSimService.validate(file);
      setReport(res.report);
    } catch (err) {
      showError(err.message || 'Could not check the file');
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    setBusy('save');
    try {
      const res = await adminMicroSimService.createDraft(file, changeNote.trim());
      onSaved(res.version);
    } catch (err) {
      showError(err.message === 'invalid_content' ? 'The file has problems — check it again.' : (err.message || 'Could not save the draft'));
      setBusy('');
    }
  };

  const download = async (which) => {
    try {
      await (which === 'example' ? adminMicroSimService.downloadExample() : adminMicroSimService.downloadTemplate());
    } catch (err) {
      showError(err.message || 'Download failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={() => download('template')}>
          <Download size={14} className="mr-1.5" /> Blank Excel template
        </Button>
        <Button variant="secondary" size="sm" onClick={() => download('example')}>
          <Download size={14} className="mr-1.5" /> Example: Message Matters
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Topic file (.xlsx or .json)</span>
          <input
            type="file"
            accept=".xlsx,.json,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => choose(e.target.files?.[0])}
            className="mt-1 block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-neutral-100 file:px-3 file:py-2 file:text-sm file:font-semibold"
          />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">What changed? (optional)</span>
          <input
            type="text"
            value={changeNote}
            maxLength={500}
            onChange={(e) => setChangeNote(e.target.value)}
            placeholder="e.g. Clearer feedback notes in round 2"
            className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={check} disabled={!file || Boolean(busy)} loading={busy === 'check'}>
          <FileUp size={15} className="mr-1.5" /> Check file
        </Button>
        {report?.valid && (
          <Button variant="primary-filled" onClick={save} disabled={Boolean(busy)} loading={busy === 'save'}>
            Save as draft {report.summary?.isNewTopic ? '(new topic)' : `v${report.summary?.nextVersion}`}
          </Button>
        )}
      </div>

      <MicroSimValidationReport report={report} />
    </div>
  );
};

MicroSimUploadPanel.propTypes = { onSaved: PropTypes.func.isRequired };

export default MicroSimUploadPanel;
